import { useEffectWithController, usePromise } from '@carvana/vdp-react-hooks';
import { useContext, useRef, useState } from 'react';
import RouteContext from '~/contexts/RouteContext';
import { SeoContent, SeoLinks, SeoReviews } from '~/contracts/Seo';
import { SeoProps } from '~/server-props/contracts';
import {
  fetchSeoContent,
  fetchSeoLinks,
  fetchSeoReviews
} from '~/services/seoService';

function useEffectFetch<T>(
  initialState: T,
  client: (_pathname: string, _controller: AbortController) => Promise<T>
): T {
  const { currentPath } = useContext(RouteContext);
  const mounted = usePromise();
  const prevPathname = useRef(currentPath);
  const [data, setData] = useState(initialState);

  useEffectWithController(
    controller => {
      // Fetch if server side request failed or filters are applied
      if (!initialState || currentPath !== prevPathname.current)
        mounted(client(currentPath, controller)).then(locationData => {
          prevPathname.current = currentPath;
          setData(locationData);
        });
    },
    [client, initialState, mounted, currentPath]
  );

  return data;
}

function useSeoData(initialSeoProps?: SeoProps) {
  const seoContent = useEffectFetch<SeoContent | undefined>(
    initialSeoProps?.content,
    fetchSeoContent
  );
  const seoLinks = useEffectFetch<SeoLinks | undefined>(
    initialSeoProps?.links,
    fetchSeoLinks
  );
  const seoReviews = useEffectFetch<SeoReviews | undefined>(
    initialSeoProps?.reviews,
    fetchSeoReviews
  );

  return {
    seoContent,
    seoLinks,
    seoReviews
  };
}

export default useSeoData;
